import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Link2, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { NAV_PRIMARY, NAV_MORE } from "@/data/constants";
import { ThreadsIcon } from "@/components/public/BrandIcons";
import { useLanguage } from "@/context/LanguageContext";

export default function Footer({ settings, card }) {
  const social = { ...(card?.socialLinks || {}) };
  Object.entries(settings?.socialLinks || {}).forEach(([k, v]) => {
    if (v) social[k] = v;
  });
  const customLinks = (settings?.customLinks?.length ? settings.customLinks : card?.customLinks) || [];
  const { lang, tr } = useLanguage();
  const L = (link) => (lang === "kn" && link.label_kn) || link.label;
  return (
    <footer className="bg-navy text-slate-300 relative grain overflow-hidden" data-testid="main-footer">
      {/* Giant outlined wordmark */}
      <p className="absolute -bottom-10 left-0 right-0 text-center font-display font-black text-[18vw] leading-none text-stroke-light select-none pointer-events-none" aria-hidden="true">
        WARD 29
      </p>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 relative">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center font-display font-black text-white text-lg">
                G
              </div>
              <div>
                <p className="font-display font-bold text-white text-lg">Girish Kumar Naidu</p>
                <p className="font-mono text-[10px] text-gold uppercase tracking-[0.2em]">GBA Hoodi Ward 29</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-slate-400 max-w-sm">
              Social worker, charitable trust leader, and aspirant candidate for GBA Hoodi Ward 29 — working for
              accessible, transparent, people-first civic leadership.
            </p>
            <div className="flex items-center gap-3 mt-7 flex-wrap" data-testid="footer-socials">
              {[
                { name: "facebook", icon: Facebook, url: social.facebook, label: "Facebook" },
                { name: "instagram", icon: Instagram, url: social.instagram, label: "Instagram" },
                { name: "twitter", icon: Twitter, url: social.twitter, label: "Twitter" },
                { name: "youtube", icon: Youtube, url: social.youtube, label: "YouTube" },
                { name: "linkedin", icon: Linkedin, url: social.linkedin, label: "LinkedIn" },
                { name: "threads", icon: ThreadsIcon, url: social.threads, label: "Threads" },
              ].filter((s) => s.url).map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.name}`}
                  className="h-10 w-10 rounded-lg border border-white/10 flex items-center justify-center text-slate-300 hover:text-white hover:bg-gold hover:border-gold transition-colors"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
              {customLinks.filter((l) => l.url).map((l, i) => (
                <a
                  key={`${l.url}-${i}`}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={l.label || "Link"}
                  title={l.label}
                  data-testid={`footer-custom-link-${i}`}
                  className="h-10 w-10 rounded-lg border border-white/10 flex items-center justify-center text-slate-300 hover:text-white hover:bg-gold hover:border-gold transition-colors"
                >
                  {l.svg ? (
                    <span className="h-4 w-4 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:fill-current" dangerouslySetInnerHTML={{ __html: l.svg }} />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">{tr("footer.explore")}</h4>
            <div className="flex flex-col gap-2">
              {NAV_PRIMARY.map((l) => (
                <Link key={l.path} to={l.path} className="text-sm text-slate-400 hover:text-gold transition-colors py-0.5">
                  {L(l)}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">{tr("footer.more")}</h4>
            <div className="flex flex-col gap-2">
              {NAV_MORE.map((l) => (
                <Link key={l.path} to={l.path} className="text-sm text-slate-400 hover:text-gold transition-colors py-0.5">
                  {L(l)}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">{tr("footer.office")}</h4>
            <div className="space-y-3 text-sm text-slate-400">
              <a href={`tel:${settings?.phone || ""}`} className="flex items-center gap-3 hover:text-gold">
                <Phone className="h-4 w-4 text-gold" /> {settings?.phone || "+91 90000 00000"}
              </a>
              <a href={`mailto:${settings?.email || ""}`} className="flex items-center gap-3 hover:text-gold">
                <Mail className="h-4 w-4 text-gold" /> {settings?.email || "office@girishnaidu.in"}
              </a>
              <p className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" /> {settings?.address || "Hoodi, Bengaluru, Karnataka"}
              </p>
            </div>
            <div className="flex flex-col gap-3 mt-7">
              <Link
                to="/submit-ward-issue"
                data-testid="footer-submit-issue"
                className="inline-flex items-center justify-between px-5 py-3 rounded-full bg-gold text-white font-bold text-sm btn-saffron"
              >
                {tr("cta.submitYourIssue")} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/track-issue"
                data-testid="footer-track-issue"
                className="inline-flex items-center justify-between px-5 py-3 rounded-full border border-white/15 text-white font-bold text-sm hover:border-gold hover:text-gold transition-colors"
              >
                {tr("cta.trackIssue")} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 relative">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 font-mono text-[11px] text-slate-500">
          <p>© {new Date().getFullYear()} GIRISH KUMAR NAIDU. ALL RIGHTS RESERVED.</p>
          <p>JOURNEY OF SERVICE · VISION FOR HOODI WARD 29</p>
        </div>
      </div>
    </footer>
  );
}
